import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { LoadingSpinner, pitchTypeDisplayName, VideoButton, ratio, percent, pitchSubTypeColor, styles } from 'yankees-react-utils';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import ReportCard from '../../../../components/ReportCard';
import { StrikeZones, useOktaQuery, usePitchVideoFn } from '../../../../data';
import StrikeZone from '../../../../components/StrikeZone';

const buildSeries = (repertoire, pitches, numeratorKey, denominatorKey, translate) => repertoire.filter((r) => r.fxtype !== 1).map((r) => ({
    name: `${pitchTypeDisplayName(r, true, true, translate)} (${percent(ratio(r[numeratorKey], r[denominatorKey]), 0)})`,
    color: pitchSubTypeColor(r),
    data: pitches.filter((p) => p.fxtype === r.fxtype && p.fxsubtype === r.fxsubtype).map((p) => ({
        x: p.platex,
        y: p.platez,
        gameid: p.gameid,
        eventseq: p.eventseq,
        pitchseq: p.pitchseq,
        istop: p.istop,
        inning: p.inning,
        outs: p.outs,
        balls: p.balls,
        strikes: p.strikes,
        fxtype: p.fxtype,
        fxsubtype: p.fxsubtype,
        marker: { symbol: p.stk ? 'circle' : 'square', radius: 4 },
    })),
}));

const StyledVideoButton = styled(VideoButton)`
    font-size: 18px !important;
    > span {
        font-size: 12px !important;
        margin-left: 3px;
    }
`;

const CountStrikeZone = ({ repertoire, isLhb, title, series, contourPoints, playAll }) => {
    const { t: translate } = useTranslation();
    const pitchVideoFn = usePitchVideoFn();

    const [visibleRepertoire, setVisibleRepertoire] = useState([...Array(repertoire.filter((r) => r.fxtype !== 1).length).keys()]);

    const legendClickFn = (data) => {
        if (data[0]) {
            if (visibleRepertoire.includes(data[0].index)) {
                setVisibleRepertoire(visibleRepertoire.filter((x) => x !== data[0].index));
            } else {
                setVisibleRepertoire([...visibleRepertoire, data[0].index]);
            }
        }
    };

    const innerPlayAll = () => {
        const pitches = series.reduce((all, s) => all.concat(s.data), []);
        const cleanRepertoire = repertoire.filter((r) => r.fxtype !== 1);
        if (visibleRepertoire.length) {
            const visiblePitches = visibleRepertoire
                .reduce((all, repertoireIndex) => all.concat(pitches.filter((p) => p.fxtype === cleanRepertoire[repertoireIndex].fxtype
                    && p.fxsubtype === cleanRepertoire[repertoireIndex].fxsubtype)), []);
            playAll(visiblePitches);
        }
    };

    const tooltipFormatter = (s, point) => {
        const data = point.options;

        return `<b>${s.name}</b><br/>${(data.istop ? `${translate('baseball.abbr-top')} `
            : `${translate('baseball.abbr-bottom')} `) + data.inning}, ${
            data.outs} ${translate('baseball.outs_one')}, ${
            data.balls}-${data.strikes}`;
    };

    return (
        <>
            <div className="d-flex justify-content-center align-items-center mb-2">
                <h4 className="mb-0 me-2">{title}</h4>
                <StyledVideoButton
                    icon={translate('common.all')}
                    onClick={innerPlayAll}
                    color={visibleRepertoire.length ? styles.theme.background.astronaut : styles.theme.background.geyser}
                    size="2.5em"
                >
                    {translate('sections.reports.pitcher.play-all')}
                </StyledVideoButton>
            </div>
            {series.length ? (
                <StrikeZone
                    enableZoom={false}
                    contourPoints={contourPoints}
                    seriesData={series}
                    height={400}
                    showlegend
                    legendposition="below"
                    yopposite
                    showlhb={isLhb}
                    pitcherView={false}
                    showoffgrid
                    pitchVideoFn={pitchVideoFn}
                    legendClickFn={legendClickFn}
                    tooltipFormatter={tooltipFormatter}
                />
            ) : (
                <div className="py-5 fs-5 font-italic d-flex justify-content-center align-items-center">
                    {translate('common.abbr-na')}
                </div>
            )}
        </>
    );
};

const PitchLocationsByCount = ({ isLhb, pitcher, repertoire, pitches, startDate, playAll }) => {
    const { t: translate } = useTranslation();

    const levelId = pitcher?.Team?.Level?.LevelID !== 8 ? 7 : 8;

    const minYear = moment(`${startDate}`).format('YYYY');
    const { data: strikeZoneContours, isLoading: isLoadingContours } = useOktaQuery(
        ['strikeZoneContours', minYear, isLhb, levelId],
        (t) => StrikeZones.getStrikeZoneContours(t, { isLhb, minYear, levelId }),
    );

    const counts = [{
        title: translate('sections.reports.pitcher.all'),
        series: buildSeries(repertoire, pitches, 'pitches', 'pitches_total', translate),
    }, {
        title: translate('sections.reports.pitcher.first-pitch'),
        series: buildSeries(repertoire, pitches.filter((x) => x.balls === 0 && x.strikes === 0), 'firstpit', 'firstpit_total', translate),
    }, {
        title: translate('sections.reports.pitcher.counts-1-1'),
        series: buildSeries(repertoire, pitches.filter((x) => x.balls === 1 && x.strikes === 1), 'cnt11', 'cnt11_total', translate),
    }, {
        title: translate('sections.reports.pitcher.counts-2-strike'),
        series: buildSeries(repertoire, pitches.filter((x) => x.strikes === 2 && x.balls !== 3), 'twostrikes', 'twostrikes_total', translate),
    }, {
        title: translate('sections.reports.pitcher.counts-3-2'),
        series: buildSeries(repertoire, pitches.filter((x) => x.balls === 3 && x.strikes === 2), 'cnt32', 'cnt32_total', translate),
    }, {
        title: translate('sections.reports.pitcher.pitcher-behind-counts'),
        series: buildSeries(repertoire, pitches.filter((x) => x.balls > x.strikes && x.balls !== 3 && x.strikes !== 2),
            'behind', 'behind_total', translate),
    }];

    if (isLoadingContours) {
        return <div className="py-5 text-center"> <LoadingSpinner /> </div>;
    }

    return (
        <ReportCard
            title={`${translate('sections.opponent.player.pitch-locations-by-count')} 
        ${translate('baseball.abbr-versus')} ${isLhb ? translate('baseball.lhh') : translate('baseball.rhh')}`}
        >
            <Row>
                {counts.map((c) => (
                    <Col md={6} key={c.title}>
                        <div className="m-4">
                            <CountStrikeZone
                                repertoire={repertoire}
                                isLhb={isLhb}
                                contourPoints={pitcher.Throws === 1 ? strikeZoneContours.rhp : strikeZoneContours.lhp}
                                title={c.title}
                                series={c.series}
                                playAll={playAll}
                            />
                        </div>
                    </Col>
                ))}
            </Row>
        </ReportCard>
    );
};

export default PitchLocationsByCount;
